/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { BANNER_AD_PLACEMENTS, BannerAdRowContainer } from '@coursera-web/promo-units';
import { DEFAULT_ARTICLE_AUTHOR, Footer, Header } from '@coursera-web/shared-articles';
import type { UrlDataProps } from '@coursera-web/shared-articles';

import { breakpoints } from '@coursera/cds-core';

import RichTextContentRenderer from 'bundles/unified-seo-content-common/components/rich-text/RichTextContentRenderer';
import ContentPageSEO from 'bundles/unified-seo-content-common/components/seo/ContentPageSEO';
import { DEFAULT_LOCALE, DEFAULT_REGION, SEO_CONTENT_PAGE_TYPE } from 'bundles/unified-seo-content-common/constants';
import type { PageDataProps } from 'bundles/unified-seo-content-common/hooks/useSeoContentContentPageQuery';
import { getCategoryBreadcrumbLink } from 'bundles/unified-seo-content-common/utils/hubPageUtils';

import _t from 'i18n!nls/resources';

export type Props = {
  hubPageLink: string;
} & Pick<UrlDataProps, 'slug' | 'subfolderLocaleCode'> &
  PageDataProps;

const styles = {
  rootContainer: css`
    background-color: var(--cds-color-neutral-background-primary);
    overflow-x: hidden;
    overflow-y: auto;
    position: relative;
    width: 100%;
  `,
  contentContainer: css`
    max-width: 782px;
    margin: 0 auto;
    padding: 0 16px;
    min-height: '100vh';
    ${breakpoints.down('xs')} {
      width: 100%;
    }
  `,
  bodyContainer: css`
    margin-bottom: 4rem;
  `,
};

const ResourceContentPage = ({ pageData, slug, subfolderLocaleCode, hubPageLink }: Props) => {
  if (!pageData) {
    return null;
  }

  const { title, intro, featuredImage, featuredImageLink, hideFeaturedImage, body, sys } = pageData;

  if (!title || !slug || !intro || !body || !body?.json) {
    return null;
  }

  const pageType = SEO_CONTENT_PAGE_TYPE.resources;

  const publishedAt = sys?.publishedAt;
  const currDate = new Date();
  const latestPublishedDate = publishedAt || currDate.toISOString();

  const bodyJson = body.json;
  const links = body?.links;

  const author = DEFAULT_ARTICLE_AUTHOR.name;

  const featuredImageCaption = featuredImage?.title;

  const countryCode = subfolderLocaleCode?.countryCode;
  const locales = [DEFAULT_LOCALE];

  const categoryBreadcrumbLinks = getCategoryBreadcrumbLink({ pageData, countryCode, pageType });
  const breadcrumbLinks = [{ href: hubPageLink, label: _t('Resources') }, ...categoryBreadcrumbLinks];

  return (
    <div data-testid="resource-content-page" css={styles.rootContainer}>
      <div css={styles.contentContainer}>
        <Header
          title={title}
          featuredImage={hideFeaturedImage ? null : featuredImage}
          featuredImageCaption={hideFeaturedImage ? null : featuredImageCaption}
          featuredImageLink={hideFeaturedImage ? null : featuredImageLink}
          subtitle={intro}
          authorNames={author}
          latestPublishedDate={latestPublishedDate}
          breadcrumbLinks={breadcrumbLinks}
        />
        <div css={styles.bodyContainer}>
          <RichTextContentRenderer body={bodyJson} links={links} />
        </div>
        <Footer authorNames={author} date={latestPublishedDate} title={title} shouldDisplayAuthorsProfile />
      </div>
      <BannerAdRowContainer placement={BANNER_AD_PLACEMENTS.ARTICLE} />
      <ContentPageSEO
        slug={slug}
        pageData={pageData}
        locales={locales}
        defaultRegionCountryCode={DEFAULT_REGION}
        hubPageLink={hubPageLink}
        subfolderLocaleCode={subfolderLocaleCode}
        pageType={pageType}
      />
    </div>
  );
};

export default ResourceContentPage;
